/*
 * Keeps the cart drawer free-shipping meter in sync with Shopify Ajax cart
 * mutations. Section rendering should already update the bar; this controller
 * makes the visible meter resilient when drawer HTML is cached or partially
 * re-rendered.
 */
(function () {
  const selector = '.cart-free-shipping[data-free-shipping-threshold]';
  const confettiColors = ['#8f5247', '#b87863', '#f0c7b5', '#f5dd72', '#7ccf9b', '#69c7ea', '#b48af0', '#f07ab4'];
  const reachedByThreshold = new Map();
  const currentByThreshold = new Map();
  let refreshTimer;
  let lastConfettiAt = 0;

  function decodeHtml(value) {
    if (!value) return '';

    const textarea = document.createElement('textarea');
    let decoded = value;
    for (let i = 0; i < 2; i += 1) {
      textarea.innerHTML = decoded;
      if (textarea.value === decoded) break;
      decoded = textarea.value;
    }
    return decoded;
  }

  function qualifyingTotal(cart) {
    if (!cart || !Array.isArray(cart.items)) return 0;

    return cart.items.reduce((sum, item) => {
      if (item.properties && item.properties._free_gift) return sum;
      return sum + (parseInt(item.final_line_price, 10) || 0);
    }, 0);
  }

  function triggerFreeShippingConfetti(bar) {
    if (bar.dataset.rewardConfettiEnabled !== 'true') return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    burstConfetti();
  }

  function burstConfetti() {
    const now = Date.now();
    if (now - lastConfettiAt < 1800) return;
    lastConfettiAt = now;

    // a storefront can set its own colours: --th-confetti: #1e3a8a, #ffd84d, ...
    const brandColors = getComputedStyle(document.body).getPropertyValue('--th-confetti').trim();
    const colors = brandColors ? brandColors.split(',').map((c) => c.trim()) : confettiColors;

    const burst = document.createElement('div');
    burst.className = 'cart-free-shipping-confetti';
    burst.setAttribute('aria-hidden', 'true');

    for (let i = 0; i < 44; i += 1) {
      const piece = document.createElement('span');
      const x = -1 * (35 + Math.random() * 360);
      const y = -1 * (70 + Math.random() * 570);
      const rotation = (Math.random() * 720 - 360).toFixed(0);
      const delay = (Math.random() * 180).toFixed(0);
      const duration = (1050 + Math.random() * 850).toFixed(0);
      const size = (5 + Math.random() * 7).toFixed(1);

      piece.style.setProperty('--x', `${x.toFixed(0)}px`);
      piece.style.setProperty('--y', `${y.toFixed(0)}px`);
      piece.style.setProperty('--rot', `${rotation}deg`);
      piece.style.setProperty('--delay', `${delay}ms`);
      piece.style.setProperty('--dur', `${duration}ms`);
      piece.style.setProperty('--size', `${size}px`);
      piece.style.setProperty('--color', colors[i % colors.length]);
      burst.appendChild(piece);
    }

    document.body.appendChild(burst);
    window.setTimeout(() => burst.remove(), 2400);
  }

  // The quantity promotion row turns into its reached state ([data-promo-reached]) when the cart
  // re-renders; celebrate only on that turn, never on a page that loads already reached.
  let promoWasReached = false;
  function watchPromotion() {
    promoWasReached = !!document.querySelector('[data-promo-reached]');
    new MutationObserver(() => {
      const reached = Array.from(document.querySelectorAll('[data-promo-reached]'));
      const visible = reached.find((el) => el.getClientRects().length > 0);
      if (visible && !promoWasReached && visible.dataset.rewardConfettiEnabled === 'true') {
        if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) burstConfetti();
      }
      promoWasReached = reached.length > 0;
    }).observe(document.body, { childList: true, subtree: true });
  }

  function updateBar(bar, current) {
    const shipThreshold = parseInt(bar.dataset.freeShippingThreshold, 10) || 0;
    if (!shipThreshold) return;

    const giftThreshold = parseInt(bar.dataset.giftThreshold, 10) || 0;
    const giftActive = giftThreshold > shipThreshold;

    const shipReached = current >= shipThreshold;
    const giftReached = giftActive && current >= giftThreshold;

    // Evenly-spaced milestones with a segmented fill: each segment fills as the
    // cart climbs toward that milestone's threshold (shipping = first half,
    // gift = second half), so the fill front reaches a dot exactly when met.
    let pct;
    let remaining;
    let stage;
    if (giftActive) {
      if (giftReached) {
        pct = 100;
        stage = 'done';
        remaining = 0;
      } else if (shipReached) {
        const span = giftThreshold - shipThreshold || 1;
        pct = 50 + ((current - shipThreshold) / span) * 50;
        stage = 'gift';
        remaining = giftThreshold - current;
      } else {
        pct = (current / shipThreshold) * 50;
        stage = 'shipping';
        remaining = shipThreshold - current;
      }
    } else if (shipReached) {
      pct = 100;
      stage = 'done';
      remaining = 0;
    } else {
      pct = (current / shipThreshold) * 100;
      stage = 'shipping';
      remaining = shipThreshold - current;
    }
    pct = Math.max(0, Math.min(100, Math.round(pct * 10) / 10));

    // Confetti fires when EITHER milestone is newly reached (free shipping or gift).
    const key = `${shipThreshold}:${giftThreshold}`;
    const fallbackCurrent = parseInt(bar.dataset.freeShippingCurrent, 10) || 0;
    const prev = reachedByThreshold.get(key) || {
      ship: fallbackCurrent >= shipThreshold,
      gift: giftActive && fallbackCurrent >= giftThreshold,
    };

    bar.dataset.freeShippingCurrent = String(current);
    bar.classList.toggle('cart-free-shipping--reached', stage === 'done');
    reachedByThreshold.set(key, { ship: shipReached, gift: giftReached });

    if ((shipReached && !prev.ship) || (giftReached && !prev.gift)) {
      triggerFreeShippingConfetti(bar);
    }

    const format = bar.dataset.moneyFormat || '€{{amount}}';
    const remainingMoney = formatMoney(remaining, format);

    const message = bar.querySelector('.cart-free-shipping__message');
    const messageText = (message && message.querySelector('.cart-free-shipping__message-text')) || message;
    if (messageText) {
      if (stage === 'shipping') {
        messageText.innerHTML = (decodeHtml(bar.dataset.msgShipping) || '[amount]').replace('[amount]', remainingMoney);
      } else if (stage === 'gift') {
        messageText.innerHTML = (decodeHtml(bar.dataset.msgGift) || '[amount]').replace('[amount]', remainingMoney);
      } else {
        messageText.innerHTML = decodeHtml(bar.dataset.msgDone) || messageText.innerHTML;
      }
    }

    const freeLabel = decodeHtml(bar.dataset.amountFreeLabel) || 'Free';
    const remainingLabel = decodeHtml(bar.dataset.amountRemainingLabel) || 'left';
    const currentEl = bar.querySelector('.cart-free-shipping__amount-current');
    if (currentEl) currentEl.textContent = stage === 'done' ? freeLabel : remainingMoney;
    const goalEl = bar.querySelector('.cart-free-shipping__amount-goal');
    if (goalEl) goalEl.textContent = stage === 'done' ? '' : remainingLabel;

    const track = bar.querySelector('.cart-free-shipping__track');
    if (track) track.setAttribute('aria-valuenow', String(Math.round(pct)));

    const fill = bar.querySelector('.cart-free-shipping__fill');
    if (fill) fill.style.width = `${pct}%`;

    const shipDot = bar.querySelector('.cart-free-shipping__milestone[data-milestone="shipping"]');
    if (shipDot) shipDot.classList.toggle('cart-free-shipping__milestone--reached', shipReached);
    const giftDot = bar.querySelector('.cart-free-shipping__milestone[data-milestone="gift"]');
    if (giftDot) giftDot.classList.toggle('cart-free-shipping__milestone--reached', giftReached);
  }

  function updateBars(cart) {
    const bars = document.querySelectorAll(selector);
    if (!bars.length) return;

    const current = qualifyingTotal(cart);
    bars.forEach((bar) => updateBar(bar, current));
  }

  function updateGiftNoteCounter(textarea) {
    const note = textarea.closest('.cart__note');
    const counter = note ? note.querySelector('[data-cart-note-counter]') : null;
    if (!counter) return;

    const limit = parseInt(counter.dataset.limit || textarea.getAttribute('maxlength'), 10) || 200;
    counter.textContent = `${Math.min(textarea.value.length, limit)}/${limit}`;
  }

  function updateGiftNoteCounters() {
    document.querySelectorAll('.cart__gift-note textarea[maxlength]').forEach(updateGiftNoteCounter);
  }

  function updateDrawerScrollChrome(scroller) {
    if (!scroller) return;

    const drawer = scroller.closest('cart-drawer');
    const bars = drawer ? drawer.querySelectorAll(selector) : document.querySelectorAll(selector);
    const isScrolled = scroller.scrollTop > 8;

    bars.forEach((bar) => {
      bar.classList.toggle('cart-free-shipping--scrolled', isScrolled);
    });

    // Connect the free-shipping bar to the cart header: when scrolled, the header
    // takes the same solid cart colour so the two read as one continuous surface.
    if (drawer) drawer.classList.toggle('is-cart-scrolled', isScrolled);
  }

  // The free-shipping bar sits OUTSIDE the scroll area (so it never rides the
  // scroll bounce). The scroller is pulled up underneath it by exactly the bar's
  // height so items still scroll behind it — keep that offset in sync here.
  function syncFreeShippingHeight(scroller) {
    const drawer = scroller.closest('cart-drawer');
    const bar = drawer ? drawer.querySelector('.cart-free-shipping') : null;
    const h = bar ? Math.round(bar.getBoundingClientRect().height) : 0;
    scroller.style.setProperty('--fsb-h', `${h}px`);
  }

  function bindDrawerScrollChrome() {
    document.querySelectorAll('cart-drawer-items').forEach((scroller) => {
      if (!scroller.dataset.freeShippingScrollBound) {
        scroller.dataset.freeShippingScrollBound = 'true';
        scroller.addEventListener('scroll', () => updateDrawerScrollChrome(scroller), { passive: true });
      }
      syncFreeShippingHeight(scroller);
      updateDrawerScrollChrome(scroller);
    });
  }

  function revealGiftNote(details) {
    const scroller = details && details.closest('cart-drawer-items');
    if (!scroller) return;

    window.setTimeout(() => {
      const scrollerRect = scroller.getBoundingClientRect();
      const detailsRect = details.getBoundingClientRect();
      const bottomGap = 16;
      const overflow = detailsRect.bottom - (scrollerRect.bottom - bottomGap);

      if (overflow > 0) {
        scroller.scrollTo({
          top: scroller.scrollTop + overflow,
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        });
      }
    }, 80);
  }

  async function refresh() {
    if (!document.querySelector(selector)) return;

    try {
      const response = await fetch(`${routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
      updateBars(await response.json());
    } catch (error) {
      console.error('[cart-progress]', error);
    }
  }

  function scheduleRefresh(delay = 120) {
    window.clearTimeout(refreshTimer);
    refreshTimer = window.setTimeout(refresh, delay);
  }

  function cartPageSectionsToRender() {
    const items = document.getElementById('main-cart-items');
    const footer = document.getElementById('main-cart-footer');
    const sections = [
      {
        id: 'main-cart-items',
        section: items ? items.dataset.id : '',
        selector: '.js-contents',
      },
      {
        id: 'main-cart-footer',
        section: footer ? footer.dataset.id : '',
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
    ];

    return sections.filter((section) => section.section && document.getElementById(section.id));
  }

  function sectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector)?.innerHTML;
  }

  function renderCartPageSections(sections) {
    if (!sections) return;

    cartPageSectionsToRender().forEach((section) => {
      const target = document.getElementById(section.id);
      const sectionHTML = sections[section.section];
      if (!target || !sectionHTML) return;

      const targetElement = target.querySelector(section.selector) || target;
      const nextHTML = sectionInnerHTML(sectionHTML, section.selector);
      if (targetElement && typeof nextHTML === 'string') {
        targetElement.innerHTML = nextHTML;
      }
    });

    updateGiftNoteCounters();
    scheduleRefresh(80);
  }

  if (!customElements.get('cart-page-product-form')) {
    customElements.define(
      'cart-page-product-form',
      class CartPageProductForm extends HTMLElement {
        constructor() {
          super();

          this.form = this.querySelector('form');
          this.submitButton = this.querySelector('[type="submit"]');
          this.submitButtonText = this.submitButton ? this.submitButton.querySelector('span') : null;
          this.hideErrors = this.dataset.hideErrors === 'true';

          if (!this.form || !this.submitButton) return;

          const variantIdInput = this.form.querySelector('[name="id"]');
          if (variantIdInput) variantIdInput.disabled = false;
          this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        }

        onSubmitHandler(event) {
          event.preventDefault();
          if (!this.form || this.submitButton.getAttribute('aria-disabled') === 'true') return;

          this.handleErrorMessage();
          this.submitButton.setAttribute('aria-disabled', true);
          this.submitButton.classList.add('loading');
          this.querySelector('.loading__spinner')?.classList.remove('hidden');

          const config = fetchConfig('javascript');
          config.headers['X-Requested-With'] = 'XMLHttpRequest';
          delete config.headers['Content-Type'];

          const formData = new FormData(this.form);
          formData.append(
            'sections',
            cartPageSectionsToRender().map((section) => section.section)
          );
          formData.append('sections_url', window.location.pathname);
          config.body = formData;

          fetch(`${routes.cart_add_url}`, config)
            .then((response) => response.json())
            .then((response) => {
              if (response.status) {
                if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
                  publish(PUB_SUB_EVENTS.cartError, {
                    source: 'cart-page-product-form',
                    productVariantId: formData.get('id'),
                    errors: response.errors || response.description,
                    message: response.message,
                  });
                }
                this.handleErrorMessage(response.description);
                this.error = true;
                return;
              }

              renderCartPageSections(response.sections);
              if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
                publish(PUB_SUB_EVENTS.cartUpdate, {
                  source: 'cart-page-product-form',
                  productVariantId: formData.get('id'),
                  cartData: response,
                });
              }
              this.error = false;
            })
            .catch((error) => {
              console.error('[cart-page-product-form]', error);
              this.handleErrorMessage(window.cartStrings?.error || 'There was an error while updating your cart.');
              this.error = true;
            })
            .finally(() => {
              this.submitButton.classList.remove('loading');
              if (!this.error) this.submitButton.removeAttribute('aria-disabled');
              this.querySelector('.loading__spinner')?.classList.add('hidden');
              if (typeof CartPerformance !== 'undefined') {
                CartPerformance.measureFromEvent('cart-page-add:user-action', event);
              }
            });
        }

        handleErrorMessage(errorMessage = false) {
          if (this.hideErrors) return;

          this.errorMessageWrapper =
            this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
          if (!this.errorMessageWrapper) return;
          this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

          this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);
          if (errorMessage) this.errorMessage.textContent = errorMessage;
        }
      }
    );
  }

  document.addEventListener('DOMContentLoaded', () => {
    // THEMERIE: the drawer renders the cart as JSON; fetch only when it isn't there
    const state = typeof readCartState === 'function' ? readCartState(document) : null;
    if (state) updateBars(state);
    else scheduleRefresh(0);
    updateGiftNoteCounters();
    bindDrawerScrollChrome();
    watchPromotion();
  });

  window.addEventListener(
    'resize',
    () => document.querySelectorAll('cart-drawer-items').forEach(syncFreeShippingHeight),
    { passive: true }
  );

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      // THEMERIE: a full cart (cart state, or a /cart/change and /cart/update response) needs no refetch
      const cart = event && (event.cart || event.cartData);
      const full = cart && Array.isArray(cart.items) && typeof cart.item_count === 'number';
      if (cart && Array.isArray(cart.items)) updateBars(cart);
      if (!full) scheduleRefresh(160);
      window.setTimeout(bindDrawerScrollChrome, 180);
    });
  }

  document.addEventListener('input', (event) => {
    if (event.target.matches('.cart__gift-note textarea[maxlength]')) {
      updateGiftNoteCounter(event.target);
    }
  });

  document.addEventListener('focusin', (event) => {
    const textarea = event.target.closest('cart-drawer .cart__gift-note textarea');
    if (textarea) revealGiftNote(textarea.closest('.cart__gift-note'));
  });

  document.addEventListener('click', (event) => {
    const giftSummary = event.target.closest('cart-drawer .cart__gift-note summary');
    if (giftSummary) revealGiftNote(giftSummary.closest('.cart__gift-note'));

    if (event.target.closest('cart-drawer')) {
      scheduleRefresh(700);
      window.setTimeout(bindDrawerScrollChrome, 120);
    }
  });
})();
